// hooks/useActionBasedUndoRedo.ts

import { useState, useCallback, useRef } from 'react';

// add this helper type:
type ActionContext = { selectedTradeIds?: string[] };

// extend AllocationAction with context
export type AllocationAction =
  | ({ type: 'ADD_ALLOCATION'; tradeId: string; portfolioId: string; qty: number } & ActionContext)
  | ({ type: 'REMOVE_ALLOCATION'; tradeId: string; portfolioId: string; qty: number } & ActionContext)
  | ({ type: 'BULK_ADD'; allocations: Array<{ tradeId: string; portfolioId: string; qty: number }> } & ActionContext)
  | ({ type: 'BULK_REMOVE'; allocations: Array<{ tradeId: string; portfolioId: string; qty: number }> } & ActionContext);

interface HistoryEntry {
  action: AllocationAction;
  inverse: AllocationAction; // The action needed to undo this action
  description: string;
  timestamp: number;
  userId?: string;
}

interface ActionBasedUndoRedoOptions {
  maxHistorySize?: number;
  maxHistoryAge?: number;
  onApplyAction?: (action: AllocationAction) => Promise<boolean>;
}

export function useActionBasedUndoRedo(
  options: ActionBasedUndoRedoOptions = {}
) {
  const {
    maxHistorySize = 75,
    maxHistoryAge = 240, // 4 hours in minutes
    onApplyAction, // Callback to apply the action to Firestore
  } = options;

  const historyRef = useRef<HistoryEntry[]>([]);
  const currentIndexRef = useRef(-1); // -1 means we're at the latest state

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [undoLoading, setUndoLoading] = useState(false);
  const [redoLoading, setRedoLoading] = useState(false);
  const [, forceUpdate] = useState({});

  // Create inverse action for undo functionality
  const createInverseAction = (action: AllocationAction): AllocationAction => {
    const ctx = { selectedTradeIds: action.selectedTradeIds }; // keep context
    switch (action.type) {
      case 'ADD_ALLOCATION':
        return { type: 'REMOVE_ALLOCATION', tradeId: action.tradeId, portfolioId: action.portfolioId, qty: action.qty, ...ctx };
      case 'REMOVE_ALLOCATION':
        return { type: 'ADD_ALLOCATION', tradeId: action.tradeId, portfolioId: action.portfolioId, qty: action.qty, ...ctx };
      case 'BULK_ADD':
        return { type: 'BULK_REMOVE', allocations: action.allocations, ...ctx };
      case 'BULK_REMOVE':
        return { type: 'BULK_ADD', allocations: action.allocations, ...ctx };
      default:
        throw new Error(`Unknown action type`);
    }
  };

  // Push a new action to history
  const pushAction = useCallback((
    action: AllocationAction,
    description: string,
    userId?: string
  ) => {
    // Clean up old entries first
    const now = Date.now();
    historyRef.current = historyRef.current.filter(entry => {
      const ageInMinutes = (now - entry.timestamp) / (1000 * 60);
      return ageInMinutes < maxHistoryAge;
    });

    // If we're not at the end of history, remove everything after current position
    if (currentIndexRef.current < historyRef.current.length - 1) {
      historyRef.current = historyRef.current.slice(0, Math.max(0, currentIndexRef.current + 1));
    }
    // Add new entry
    const newEntry: HistoryEntry = {
      action,
      inverse: createInverseAction(action),
      description,
      timestamp: now,
      userId,
    };

    historyRef.current.push(newEntry);

    // Limit history size
    if (historyRef.current.length > maxHistorySize) {
      historyRef.current = historyRef.current.slice(-maxHistorySize);
    }

    // Reset to latest position
    currentIndexRef.current = historyRef.current.length - 1;

    // Update UI state
    setCanUndo(historyRef.current.length > 0);
    setCanRedo(false);
    forceUpdate({});
  }, [maxHistorySize, maxHistoryAge]);

  // Undo the last action
  const undo = useCallback(async () => {
    // currentIndexRef points at the last-applied entry (>=0) or -1 if none
    if (currentIndexRef.current < 0 || currentIndexRef.current >= historyRef.current.length) {
      return null;
    }

    const entryToUndo = historyRef.current[currentIndexRef.current];

    setUndoLoading(true);
    let applied = true;
    try {
      if (onApplyAction) {
        applied = await onApplyAction(entryToUndo.inverse); // must return boolean
      }
    } finally {
      setUndoLoading(false);
    }

    // If nothing was applied (e.g., LOCKED), do NOT move pointer
    if (!applied) {
      // refresh derived flags so UI stays consistent
      setCanUndo(currentIndexRef.current >= 0);
      setCanRedo(currentIndexRef.current < historyRef.current.length - 1);
      forceUpdate({});
      return null;
    }

    // Move back only on success
    currentIndexRef.current--;

    setCanUndo(currentIndexRef.current >= 0);
    setCanRedo(true);
    forceUpdate({});

    return entryToUndo.inverse;
  }, [onApplyAction]);

  // Redo the next action
  const redo = useCallback(async () => {
    // compute next index but DO NOT mutate pointer yet
    const nextIndex = currentIndexRef.current + 1;
    if (nextIndex >= historyRef.current.length) {
      return null;
    }

    const entryToRedo = historyRef.current[nextIndex];

    setRedoLoading(true);
    let applied = true;
    try {
      if (onApplyAction) {
        applied = await onApplyAction(entryToRedo.action); // must return boolean
      }
    } finally {
      setRedoLoading(false);
    }

    // If nothing was applied (e.g., LOCKED), keep pointer where it is
    if (!applied) {
      setCanUndo(currentIndexRef.current >= 0);
      setCanRedo(currentIndexRef.current < historyRef.current.length - 1);
      forceUpdate({});
      return null;
    }

    // Advance only on success
    currentIndexRef.current = nextIndex;

    setCanUndo(true);
    setCanRedo(currentIndexRef.current < historyRef.current.length - 1);
    forceUpdate({});

    return entryToRedo.action;
  }, [onApplyAction]);

  // Get history for display
  const getHistory = useCallback(() => {
    const now = Date.now();
    return historyRef.current.map((entry, index) => {
      const ageInMinutes = Math.floor((now - entry.timestamp) / (1000 * 60));
      let ageDisplay: string;

      if (ageInMinutes < 1) {
        ageDisplay = 'Just now';
      } else if (ageInMinutes < 60) {
        ageDisplay = `${ageInMinutes} minute${ageInMinutes !== 1 ? 's' : ''} ago`;
      } else {
        const hours = Math.floor(ageInMinutes / 60);
        ageDisplay = `${hours} hour${hours !== 1 ? 's' : ''} ago`;
      }

      return {
        ...entry,
        index,
        isCurrent: index === currentIndexRef.current,
        isUndone: index > currentIndexRef.current,
        ageInMinutes,
        ageDisplay,
      };
    });
  }, []);

  // Clear all history
  const clearHistory = useCallback(() => {
    historyRef.current = [];
    currentIndexRef.current = -1;
    setCanUndo(false);
    setCanRedo(false);
    forceUpdate({});
  }, []);

  // Get statistics
  const getStats = useCallback(() => {
    return {
      totalActions: historyRef.current.length,
      currentPosition: currentIndexRef.current + 1,
      undoableActions: currentIndexRef.current + 1,
      redoableActions: historyRef.current.length - currentIndexRef.current - 1,
    };
  }, []);

  return {
    pushAction,
    undo,
    redo,
    canUndo,
    canRedo,
    getHistory,
    clearHistory,
    getStats,
    undoLoading,
    redoLoading,
  };
}